import React, { useEffect, useMemo } from 'react';

import Checkbox from 'epn-ui/umd/dist/components/CheckboxInput';
import Form, { FormInstance } from 'epn-ui/umd/dist/components/Form';
import Select from 'epn-ui/umd/dist/components/Select';

import appConfig from '@config/app.json';
import useI18n from '@i18n';
import { IGetDomainCuttersData } from '@api/types';

import BaseEmpty from '@components/base/BaseEmpty';

import { IAffiliateLinksForm } from '@pages/tools/affiliate-links/interface';
import { getOptionsValue } from '@pages/tools/affiliate-links/utils';

import useStorage from '@hooks/useStorage';
import { ALI_PUB_DATA, GOT_BY_DATA } from '../config';

interface IAffiliateCheckboxes {
    form: FormInstance<IAffiliateLinksForm>;
    domainCutters: IGetDomainCuttersData;
}

const AffiliateCheckboxes: React.FC<IAffiliateCheckboxes> = ({ form, domainCutters }) => {
    const { t: tTools } = useI18n('Tools');
    const { t: tCommon } = useI18n('Common');

    const offerId = form.getFieldValue('offer');

    const isRefOfferId = appConfig.REF_OFFERS_IDS.includes(offerId);
    const DEFAULT_REDUCE_LINK_FLAG_VALUE = !isRefOfferId;

    // checks
    const [isNeedLinkReduction, toggleNeedLinkReduction] = useStorage(
        'isNeedLinkReduction',
        DEFAULT_REDUCE_LINK_FLAG_VALUE,
    );
    const [domainCutter, setDomainCutter] = useStorage(
        'domainCutter',
        +offerId === appConfig.OFFER_IDS.ALI ? ALI_PUB_DATA : GOT_BY_DATA,
    );

    useEffect((): void => {
        if (isRefOfferId) {
            toggleNeedLinkReduction(false);
        }
    }, [isRefOfferId, toggleNeedLinkReduction]);

    // UseForm не цепляет значение после того как оно выставилось из localstorage
    useEffect((): void => {
        form.setFieldValue(
            'isNeedLinkReduction',
            isNeedLinkReduction !== null ? isNeedLinkReduction : DEFAULT_REDUCE_LINK_FLAG_VALUE,
        );
    }, [form, isNeedLinkReduction, DEFAULT_REDUCE_LINK_FLAG_VALUE]);

    useEffect(() => {
        form.setFieldValue('domainCutter', domainCutter);
    }, [domainCutter, form]);

    const domainOptions = useMemo(
        () =>
            domainCutters?.attributes?.map(attr => ({
                value: attr,
                label: attr,
            })),
        [domainCutters],
    );

    return (
        <div className="mb-8 sm:mb-0">
            <div className="flex items-center">
                <Form.Item name="isNeedLinkReduction" className="mb-0">
                    <Checkbox
                        label={tTools('Shorten link via')}
                        checked={
                            isNeedLinkReduction !== null
                                ? isNeedLinkReduction
                                : DEFAULT_REDUCE_LINK_FLAG_VALUE
                        }
                        id="AffiliateCheckboxesLinkReductionCheckbox"
                        onChange={e => {
                            toggleNeedLinkReduction(e.target.checked);
                        }}
                    />
                </Form.Item>
                <div className="ml-2">
                    {!!domainOptions?.length && (
                        <Form.Item
                            name="domainCutter"
                            rules={[{ required: isNeedLinkReduction }]}
                            className="mb-0"
                        >
                            <Select
                                id="AffiliateCheckboxesDomainCutterSelect"
                                options={domainOptions}
                                fieldNames={{ value: 'label', label: 'label' }}
                                onChange={value => {
                                    const indentValue = getOptionsValue(domainOptions, value);
                                    setDomainCutter(indentValue);
                                }}
                                notFoundContent={() => (
                                    <BaseEmpty
                                        id="AffiliateCheckboxesDomainCutterSelect"
                                        message={tCommon('Nothing found')}
                                    />
                                )}
                            />
                        </Form.Item>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AffiliateCheckboxes;
